// SPEC §6 — beats.ts is the spine. Every animated parameter in the site is a keyframed
// track evaluated at progress. Systems read the result and render; they own no animation
// state. No `if (progress > x)` branches live anywhere else.

export interface Beats {
  chroma: number // FOUNDATION §5 — the chroma curve
  flow: number // flowMode 0 turbulent | 1 constrained | 2 grid | 3 dissolve (lerps)
  active: number // fraction of the glyph pool that is visible
  spread: number // 0 = single ribbon at the hero desk, 1 = full field
  calm: number // free-column height multiplier — the air clears as the tubes capture
  reveal: number // prop birth threshold (world grows outward from the one desk)
  sheets: number // fraction of the sheet pool active
  lift: number // sheets rising off the desks (ARTIFACTS)
  lock: number // sheets locking into architecture (ARCHITECTURE)
  trace: number // floor trace ignition (ORGANIZE)
  fog: number // FogExp2-style density fed to every shader
  bgWhite: number // background lerp ground -> near-white for the final sky
}

type Key = [number, number]

function tr(keys: Key[], p: number): number {
  if (p <= keys[0][0]) return keys[0][1]
  for (let i = 0; i < keys.length - 1; i++) {
    const [p0, v0] = keys[i]
    const [p1, v1] = keys[i + 1]
    if (p <= p1) {
      const t = (p - p0) / (p1 - p0)
      const s = t * t * (3 - 2 * t)
      return v0 + (v1 - v0) * s
    }
  }
  return keys[keys.length - 1][1]
}

export function beats(p: number): Beats {
  return {
    // FOUNDATION §5: 0 / .05 / .6 / 1.0 / .55 / .25 / .15
    chroma: tr([[0, 0], [0.15, 0.05], [0.28, 0.1], [0.35, 0.6], [0.55, 1], [0.72, 0.55], [0.86, 0.25], [1, 0.15]], p),
    // MIX captures 0->1, ORGANIZE snaps 1->2 — completing WHILE the god view holds,
    // so the settled ranks are seen from above; SKY dissolves 2->3
    flow: tr([[0, 0], [0.3, 0], [0.44, 1], [0.48, 1], [0.57, 2], [0.86, 2], [0.92, 3], [1, 3]], p),
    // CREATE ramps 1 -> 40k; ARTIFACTS consumes glyphs into sheets (40k -> ~12k)
    // the last streams thicken slightly at SKY so the ascent reads as rising past
    // material, not as an empty page
    active: tr([[0, 0.012], [0.15, 1], [0.6, 1], [0.72, 0.3], [0.86, 0.2], [0.92, 0.3], [0.96, 0.16], [1, 0.08]], p),
    spread: tr([[0, 0], [0.05, 0.03], [0.15, 1]], p),
    // CREATE is a storm; through MIX the uncaptured mist settles toward the desks
    // so the canopy and the captured streams read against clearing air
    calm: tr([[0, 1], [0.3, 1], [0.42, 0.45], [0.5, 0.32], [1, 0.32]], p),
    reveal: tr([[0, 0.001], [0.05, 0.002], [0.15, 1]], p),
    sheets: tr([[0, 0], [0.58, 0], [0.72, 0.85], [0.8, 1]], p),
    lift: tr([[0.58, 0], [0.72, 1]], p),
    lock: tr([[0.72, 0], [0.86, 1]], p),
    trace: tr([[0.42, 0], [0.55, 1], [0.66, 0.9], [0.72, 0.35], [0.86, 0.1], [1, 0]], p),
    // fog DROPS into ORGANIZE: the god view is 40-100m out and peak chroma must
    // survive the air, or the whole §5 curve is inaudible at its own maximum
    fog: tr([[0, 0.016], [0.35, 0.019], [0.5, 0.013], [0.55, 0.0095], [0.62, 0.0095], [0.68, 0.014], [0.72, 0.016], [0.86, 0.02], [0.92, 0.012], [1, 0.007]], p),
    // G5: page background is --ground, never #FFFFFF. The canvas clear colour may
    // approach white for the final sky (white is reserved for the sky) but caps at 0.9.
    bgWhite: tr([[0.86, 0], [0.97, 0.8], [1, 0.9]], p),
  }
}
